"use client";

import { motion, useScroll, useTransform, type Variants } from "motion/react";
import { useInView } from "react-intersection-observer";
import { Target, TrendingUp, Award, CheckCircle, Users } from "lucide-react";
import { useRef } from "react";
import FeatureCard from "../CardFeature";

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

export default function Features() {
  const sectionRef = useRef(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const opacity = useTransform(
    scrollYProgress,
    [0, 0.3, 0.7, 1],
    [0, 1, 1, 0.8]
  );
  const scale = useTransform(scrollYProgress, [0, 0.3], [0.8, 1]);

  const features: Feature[] = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Apprenants qualifiés",
      description:
        "Nous vous envoyons uniquement les apprenants ayant un besoin précis sur votre sujet.",
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Suivi + Gamification",
      description:
        "Notre système de suivi et gamification réduit drastiquement le taux d'abandon de vos sessions.",
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Meilleure crédibilité",
      description:
        "Moins d'abandons = meilleurs avis et plus de crédibilité pour vous.",
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Coaching intégré",
      description: "Coaching intégré pour décupler l'impact de vos formations.",
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Visibilité garantie",
      description:
        "Redevenez visible dans un marché français devenu méfiant et saturé.",
    },
  ];

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0,
        delayChildren: 0,
      },
    },
  };

  const cardVariants: Variants = {
    hidden: {
      opacity: 0,
      x: -150,
      scale: 0.4,
    },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        type: "spring" as const,
        damping: 30,
        stiffness: 200,
        duration: 0.1,
        delay: 0,
      },
    },
  };

  const titleVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 25,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring" as const,
        damping: 25,
        stiffness: 150,
        duration: 0.1,
        delay: 0,
      },
    },
  };

  const lineVariants: Variants = {
    hidden: {
      scaleX: 0,
      opacity: 0,
    },
    visible: {
      scaleX: 1,
      opacity: 1,
      transition: {
        delay: 0,
        duration: 0.1,
        ease: "easeOut",
      },
    },
  };

  return (
    <section ref={sectionRef} className="pb-15 bg-white overflow-hidden">
      <motion.div
        ref={ref}
        style={{ opacity, scale }}
        className="section-container"
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2
            variants={titleVariants}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
          >
            Marre des abandons et de la méfiance du marché ?
          </motion.h2>

          <motion.div
            variants={lineVariants}
            className="w-20 h-1 bg-gradient-to-r from-primary-400 to-primary-500 mx-auto rounded-full origin-center"
          />
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="flex flex-wrap justify-center gap-6 max-w-6xl mx-auto"
        >
          {features.map((feature, index) => (
            <div
              key={index}
              className="w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]"
            >
              <FeatureCard
                {...feature}
                index={index}
                inView={inView}
                variants={cardVariants}
              />
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
